import React, { useEffect, useState } from 'react';
import styles from './DashboardTestAutomation.module.css';
import { useTheme } from '../../hooks/useTheme';
import DateRangePicker, { getToday, getTwelveMonthsAgo } from './time-range-select/DateRangePicker';
import { Tile, TileAction, useMaximizeTile } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import TestExecutionStatusSummary from './test-execution-status-summary/TestExecutionStatusSummary';
import { useTestExecutionDashboardData, useTestExecutionDashboardSummary } from './hooks/use-test-execution-dashboard-data';
import TestExecutionStatusByProductLine from './test-execution-status-by-product-line/TestExecutionStatusByProductLine';
import TestExecutionAverageTime from './test-execution-average-time/TestExecutionAverageTime';

type OverviewSectionId = 'execution-per-pl' | 'average-time-per-pl';

const DashboardTestAutomation: React.FC = () => {
  const { theme } = useTheme();
  const [startDate, setStartDate] = useState<string>(() => getTwelveMonthsAgo());
  const [endDate, setEndDate] = useState<string>(() => getToday());
  const [maximizedSection, setMaximizedSection] = useState<OverviewSectionId | null>(null);
  const { setMaximizedTileId } = useMaximizeTile();

  const handleMaximize = (sectionId: OverviewSectionId | null) => {
    setMaximizedSection(maximizedSection === sectionId ? null : sectionId);
  };

  useEffect(() => {
    setMaximizedSection(null);
    return () => {
      setMaximizedSection(null);
      setMaximizedTileId(undefined);
    };
  }, [setMaximizedTileId]);

  const sectionClass = theme ? styles.sectionLight : styles.sectionDark;


  const summaryResponse = useTestExecutionDashboardSummary(startDate, endDate);
  const statusResponse = useTestExecutionDashboardData(startDate, endDate);

  return (
    <div className={`${styles.wrapper} ${styles.wrapperOverview}`}>
      <header className={`${styles.header} ${styles.headerWithSummary}`}>
        <div className={`${styles.headerTimeBlock} ${sectionClass}`}>
          <DateRangePicker startDate={startDate} endDate={endDate} onStartDateChange={setStartDate} onEndDateChange={setEndDate} />
        </div>
        <div className={`${styles.headerSummaryBlock} ${sectionClass}`}>
          <TestExecutionStatusSummary data={summaryResponse.data || []} isLoading={summaryResponse.isLoading} isError={summaryResponse.isError} />
        </div>
      </header>

      {maximizedSection ? (
        <section className={`${styles.maximizedSection} ${sectionClass}`}>
          {maximizedSection === 'execution-per-pl' && (
            <ReactEDSWrapper
              key="execution-per-pl"
              component={Tile}
              id="execution-per-pl"
              title="Average SW Delivery Time by Stage"
              subtitle="Average time spent per stage (in minutes)."
              onMaximize={() => handleMaximize('execution-per-pl')}
              onMinimize={() => {
                handleMaximize(null);
                setMaximizedTileId(undefined);
              }}
              actions={[<ReactEDSWrapper component={TileAction} key="maximize" maximizeTileId="execution-per-pl" />]}
            >
              <TestExecutionStatusByProductLine startDate={startDate} endDate={endDate} data={statusResponse.data || []} isLoading={statusResponse.isLoading} isError={statusResponse.isError} />
            </ReactEDSWrapper>
          )}
          {maximizedSection === 'average-time-per-pl' && (
            <ReactEDSWrapper
              key="average-time-per-pl"
              component={Tile}
              id="average-time-per-pl"
              title="Failed SW Delivery"
              subtitle="Stage failure details in the selected date range."
              onMaximize={() => handleMaximize('average-time-per-pl')}
              onMinimize={() => {
                handleMaximize(null);
                setMaximizedTileId(undefined);
              }}
              actions={[<ReactEDSWrapper component={TileAction} key="maximize" maximizeTileId="average-time-per-pl" />]}
            >
              <div className={styles.maximizedTableTileContent}>
                <TestExecutionAverageTime startDate={startDate} endDate={endDate} data={statusResponse.data || []} isLoading={statusResponse.isLoading} isError={statusResponse.isError} />
              </div>
            </ReactEDSWrapper>
          )}
        </section>
      ) : (
        <div className={styles.gridContainer}>
          <ReactEDSWrapper
            key="execution-per-pl"
            component={Tile}
            id="execution-per-pl"
            title="Test Executions per Product Line"
            subtitle="Number of Test Executions per Product Line."
            className={`${styles.overviewLeftTop} ${sectionClass}`}
            onMaximize={() => handleMaximize('execution-per-pl')}
            onMinimize={() => handleMaximize('execution-per-pl')}
            actions={[<ReactEDSWrapper component={TileAction} key="maximize" maximizeTileId="execution-per-pl" />]}
          >
            <div className={styles.overviewTileContentFixed}>
              <TestExecutionStatusByProductLine startDate={startDate} endDate={endDate} data={statusResponse.data || []} isLoading={statusResponse.isLoading} isError={statusResponse.isError} />
            </div>
          </ReactEDSWrapper>
          <ReactEDSWrapper
            key="average-time-per-pl"
            component={Tile}
            id="average-time-per-pl"
            title="Average Test Execution Time per Product Line"
            subtitle="Average time spent per Test Execution (in minutes)."
            className={`${styles.overviewRightTop} ${sectionClass}`}
            onMaximize={() => handleMaximize('average-time-per-pl')}
            onMinimize={() => handleMaximize('average-time-per-pl')}
            actions={[<ReactEDSWrapper component={TileAction} key="maximize" maximizeTileId="average-time-per-pl" />]}
          >
            <TestExecutionAverageTime startDate={startDate} endDate={endDate} data={statusResponse.data || []} isLoading={statusResponse.isLoading} isError={statusResponse.isError} />
          </ReactEDSWrapper>
        </div>
      )}
    </div>
  );
};

export default DashboardTestAutomation;
